import React, { Fragment, useState, useEffect, useRef } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import PortalMenu from "../Common/PortalMenu";
import AppPagination from "../Common/AppPagination";
import { useSelector } from "react-redux";
import PermissionWrapper from "../PermissionWrapper";
import { Row, Col, Card, Table, Badge, Dropdown, Modal, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import api, { getFileUrl } from "../../../api";
import swal from "sweetalert";
import { useLocation, useNavigate } from "react-router-dom";
import { useProject } from "../../../context/ProjectContext";
import FilePreview from "../Common/FilePreview";
import SignatureModal from "../Common/SignatureModal";
import VerifiedCopyActions from "../Common/VerifiedCopyActions";
const PurchaseOrderList = () => {
 const [showModal, setShowModal] = useState(false);
 const user = useSelector((state) => state.auth?.userDetails);
 const [selectedPO, setSelectedPO] = useState(null);
 const [orders, setOrders] = useState([]);
 const [loading, setLoading] = useState(false);
 const [approvingId, setApprovingId] = useState(null); // Added for loading state during approval
const [previewingPdf, setPreviewingPdf] = useState(false);
const [showSignModal, setShowSignModal] = useState(false);
const [approveId, setApproveId] = useState(null);
const { workOrderId, setWorkOrderId, projectId, setProjectId } = useProject(); // Added projectId
 const location = useLocation();
  const navigate = useNavigate();
 const [pagination, setPagination] = useState({
 currentPage: 1,
 totalPages: 1,
 totalCount: 0,
 hasPrevPage: false,
 hasNextPage: false,
 });

 const [projects, setProjects] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [vendors, setVendors] = useState([]);

 const fetchRequestIdRef = useRef(0);

  const [filters, setFilters] = usePersistentFilters("purchase_orders_filters", () => {
    const params = new URLSearchParams(location.search);
    return {
      search: "",
      status: "",
      project: params.get("project") || "",
      workOrder: params.get("workOrder") || "",
      vendor: "",
      fromDate: "",
      toDate: "",
      page: 1,
      limit: 10,
    };
  });
  const currentRole = String(user?.role?.name || user?.roleName || user?.role || "").toLowerCase();
  const canManagePO =
    currentRole === "admin" ||
    Boolean(
      user?.permissions?.some((permission) =>
        ["create:pos", "edit:pos", "approve:pos", "reject:pos", "delete:pos"].includes(permission)
      )
    );

  // Load Projects & Vendors on mount
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const [projRes, vendorRes] = await Promise.all([
          api.get("/projects"),
          api.get("/vendors?limit=200"),
        ]);
        setProjects(projRes.data?.data || projRes.data || []);
        setVendors(vendorRes.data?.data || vendorRes.data || []);
      } catch (err) {
        console.error("Error loading filter dropdown data:", err);
      }
    };
    loadDropdownData();
  }, []);

  // Load Work Orders when project changes
  useEffect(() => {
    const loadWorkOrders = async () => {
      try {
        const params = { limit: 200 };
        if (filters.project) params.project = filters.project;
        const res = await api.get("/work-orders", { params });
        const list = res.data?.data || res.data || [];
        setWorkOrders(list);

        // If workOrder was set via URL without project, auto-fill project from the matched workOrder
        if (filters.workOrder && !filters.project) {
          const matched = list.find((w) => String(w._id) === String(filters.workOrder));
          const pId = matched?.project?._id || (typeof matched?.project === "string" ? matched.project : "");
          if (pId) {
            setFilters((prev) => ({ ...prev, project: pId }));
            setProjectId(pId);
          }
        }
      } catch (err) {
        console.error("Error loading work orders:", err);
      }
    };
    loadWorkOrders();
  }, [filters.project, filters.workOrder]);

  // Sync URL params when location.search changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const wo = params.get("workOrder") || "";
    const proj = params.get("project") || "";

    setWorkOrderId(wo);
    setProjectId(proj);

    setFilters((prev) => {
      if (prev.workOrder === wo && prev.project === proj) return prev;
      return {
        ...prev,
        workOrder: wo,
        project: proj,
        page: 1,
      };
    });
  }, [location.search]);

  const fetchPurchaseOrders = async () => {
    const requestId = ++fetchRequestIdRef.current;
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      if (filters.search) queryParams.append("search", filters.search);
      if (filters.status) queryParams.append("status", filters.status);
      if (filters.fromDate) queryParams.append("fromDate", filters.fromDate);
      if (filters.toDate) queryParams.append("toDate", filters.toDate);
      if (filters.project) queryParams.append("project", filters.project);
      if (filters.workOrder) queryParams.append("workOrder", filters.workOrder);
      if (filters.vendor) queryParams.append("vendor", filters.vendor);
      queryParams.append("page", filters.page);
      queryParams.append("limit", filters.limit);

      const response = await api.get(
        `/purchase-orders?${queryParams.toString()}`
      );

      // Discard stale responses if another fetch was dispatched
      if (requestId !== fetchRequestIdRef.current) return;

      if (response.data.success) {
        setOrders(response.data.data || []);
        if (response.data.pagination) {
          setPagination((prev) => ({
            ...prev,
            ...response.data.pagination,
          }));
        }
      }
    } catch (error) {
      if (requestId !== fetchRequestIdRef.current) return;
      console.error("Error fetching purchase orders:", error);
      swal("Error", "Failed to fetch purchase orders", "error");
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchPurchaseOrders();
 }, [
   filters.page,
   filters.limit,
   filters.status,
   filters.project,
   filters.workOrder,
   filters.vendor,
   filters.fromDate,
   filters.toDate,
 ]);
 const handlePreviewPDF = async (id) => {
 try {
 setPreviewingPdf(true);

 const res = await api.get(`/purchase-orders/${id}/pdf`, {
 responseType: "blob",
 });

 const fileURL = window.URL.createObjectURL(
 new Blob([res.data], { type: "application/pdf" })
 );

 window.open(fileURL, "_blank");
 } catch (err) {
 console.error("PO PDF preview error:", err);
 swal("Error", "Failed to preview Purchase Order PDF", "error");
 } finally {
 setPreviewingPdf(false);
 }
};

 const handleDownloadPDF = async (id) => {
 try {
 const res = await api.get(`/purchase-orders/${id}/pdf`, {
 responseType: "blob",
 });

 const url = window.URL.createObjectURL(new Blob([res.data]));
 const link = document.createElement("a");
 link.href = url;
 link.download = `PO-${id}.pdf`;
 link.click();
 } catch (err) {
 swal("Error", "Failed to download PDF", "error");
 }
 };

 const handleApprove = async (id) => {
 const confirm = await swal({
 title: "Approve Purchase Order?",
 text: "Signature is required for approval.",
 icon: "info",
 buttons: ["Cancel", "Provide Signature"],
 });

 if (!confirm) return;

 setApproveId(id);
 setShowSignModal(true);
 };

 const handleSaveSignature = async (file) => {
 try {
 setLoading(true);

 const formData = new FormData();
 formData.append("signature", file);

 const res = await api.patch(
 `/purchase-orders/${approveId}/approve`,
 formData,
 {
 headers: {
 "Content-Type": "multipart/form-data",
 },
 },
 );

 if (res.data.success) {
 swal("Approved!", "Purchase order has been issued.", "success");
 setShowSignModal(false);
 setApproveId(null);
 fetchPurchaseOrders();
 }
 } catch (error) {
 swal(
 "Error",
 error.response?.data?.message || "Failed to approve purchase order",
 "error",
 );
 } finally {
 setLoading(false);
 }
 };

 const handleReject = async (id) => {
 const remarks = prompt("Enter rejection reason:");
 if (!remarks) return;

 try {
 const res = await api.patch(`/purchase-orders/${id}/reject`, { remarks });
 if (res.data.success) {
 swal("Rejected!", "Purchase order rejected successfully", "success");
 fetchPurchaseOrders();
 }
 } catch (err) {
 swal("Error", "Failed to reject", "error");
 }
 };

const handleDelete = async (id) => {
 const confirm = await swal({
 title: "Are you sure?",
 text: "This purchase order will be deleted permanently",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });

 if (!confirm) return;

 try {
 const res = await api.delete(`/purchase-orders/${id}`);

 if (res.data.success) {
 swal("Deleted!", res.data.message, "success");
 fetchPurchaseOrders();
 }
 } catch (error) {
 // THIS IS THE IMPORTANT PART
 swal(
 "Cannot Delete",
 error.response?.data?.message || "Failed to delete purchase order",
 "warning"
 );
 }
};


 const handleView = async (id) => {
 try {
 const response = await api.get(`/purchase-orders/${id}`);
 if (response.data.success) {
 setSelectedPO(response.data.data);
 setShowModal(true);
 }
 } catch (error) {
 console.error("Error fetching PO details:", error);
 swal(
 "Error",
 error.response?.data?.message ||
 "Failed to load purchase order details",
 "error"
 );
 }
 };

 const handleSearch = () => {
 setFilters((prev) => ({ ...prev, page: 1 }));
 fetchPurchaseOrders();
 };

 const handleResetFilters = () => {
    setFilters({
      search: "",
      status: "",
      project: "",
      workOrder: "",
      vendor: "",
      fromDate: "",
      toDate: "",
      page: 1,
      limit: 10,
    });
    setProjectId("");
    setWorkOrderId("");
    navigate("/purchase-orders", { replace: true });
  };

 const hasActiveFilters = Boolean(
 filters.search ||
 filters.status ||
 filters.project ||
 filters.workOrder ||
 filters.vendor ||
 filters.fromDate ||
 filters.toDate
 );

 const handlePageChange = (newPage) => {
 if (
 newPage < 1 ||
 (pagination.totalPages && newPage > pagination.totalPages)
 )
 return;

 setFilters((prev) => ({ ...prev, page: newPage }));
 };

 const formatDate = (dateString) => {
 if (!dateString) return "—";
 try {
 return new Date(dateString).toLocaleDateString("en-IN", {
 year: "numeric",
 month: "short",
 day: "numeric",
 });
 } catch {
 return "—";
 }
 };

 const formatCurrency = (amount) => {
 if (amount === null || amount === undefined) return "₹0";
 try {
 return new Intl.NumberFormat("en-IN", {
 style: "currency",
 currency: "INR",
 }).format(amount);
 } catch {
 return `₹${amount}`;
 }
 };

 const getStatusBadge = (status) => {
 const statusMap = {
 Draft: "secondary",
 Issued: "info",
 Accepted: "success",
 "Partially Delivered": "warning",
 Delivered: "primary",
 Closed: "dark",
 };
 return statusMap[status] || "secondary";
 };

 const getIndentReferenceText = (po) => {
 const refs = Array.isArray(po?.indentReferences) && po.indentReferences.length
 ? po.indentReferences
 : po?.indentReference
 ? [po.indentReference]
 : [];

 return refs
 .map((indent) =>
 indent?.indentNumber ||
 indent?.mirNumber ||
 (typeof indent === "string" ? indent : "")
 )
 .filter(Boolean)
 .join(", ") || "N/A";
 };

 const svgMenu = (
 <svg width="20px" height="20px" viewBox="0 0 24 24">
 <circle fill="#000" cx="5" cy="12" r="2"></circle>
 <circle fill="#000" cx="12" cy="12" r="2"></circle>
 <circle fill="#000" cx="19" cy="12" r="2"></circle>
 </svg>
 );

 return (
 <Fragment>
 <PageTitle activeMenu="Purchase Orders" motherMenu="Procurement" />

 {/* List */}
 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title className="mb-0">Purchase Order List</Card.Title>
 <PermissionWrapper user={user} required={["create:pos"]}>
 <div className="d-flex gap-2 ms-auto">
 <Link
 to={`/purchase-orders/add${workOrderId ? `?workOrder=${workOrderId}` : ""}`}
 className="btn btn-primary"
 >
 + Create PO
 </Link>
 <Link
 to={`/contractor-billing${workOrderId ? `?workOrder=${workOrderId}` : ""}`}
 className="btn btn-outline-info"
 >
 Contractor Billing
 </Link>
 <Button variant="light" onClick={() => window.history.back()}>
 <i className="fa fa-arrow-left me-1"></i> Back
 </Button>
 </div>
 </PermissionWrapper>
 </Card.Header>

 <div className="d-flex flex-wrap align-items-end gap-2 px-4 py-3 border-bottom">
 <div className="d-flex flex-column" style={{ minWidth: "170px", flex: "1 1 170px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Search PO</label>
 <div className="d-flex align-items-center gap-1">
 <input
 type="text"
 className="form-control form-control-sm"
 placeholder="PO No / Item..."
 value={filters.search}
 onChange={(e) =>
 setFilters((prev) => ({
 ...prev,
 search: e.target.value,
 }))
 }
 onKeyPress={(e) => e.key === "Enter" && handleSearch()}
 />
 <button
 className="btn btn-sm btn-primary px-2"
 style={{ height: "31px" }}
 onClick={handleSearch}
 title="Search"
 >
 <i className="fas fa-search"></i>
 </button>
 </div>
 </div>

 <div className="d-flex flex-column" style={{ minWidth: "160px", flex: "1 1 160px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Project</label>
 <select
 className="form-select form-select-sm"
 value={filters.project}
 onChange={(e) => {
 const pId = e.target.value;
 setFilters((prev) => ({
 ...prev,
 project: pId,
 workOrder: "",
 page: 1,
 }));
 if (pId) setProjectId(pId);
 }}
 >
 <option value="">All Projects</option>
  {filters.project && !projects.some((p) => String(p._id) === String(filters.project)) && (
    <option value={filters.project}>Selected Project</option>
  )}
 {projects.map((p) => (
 <option key={p._id} value={p._id}>
 {p.projectName || p.name}
 </option>
 ))}
 </select>
 </div>

 <div className="d-flex flex-column" style={{ minWidth: "170px", flex: "1.2 1 170px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Work Order</label>
 <select
 className="form-select form-select-sm"
 value={filters.workOrder}
 onChange={(e) => {
 const woId = e.target.value;
 setFilters((prev) => ({
 ...prev,
 workOrder: woId,
 page: 1,
 }));
 if (woId) setWorkOrderId(woId);
 }}
 >
 <option value="">All Work Orders</option>
  {filters.workOrder && !workOrders.some((w) => String(w._id) === String(filters.workOrder)) && (
    <option value={filters.workOrder}>Selected Work Order</option>
  )}
 {workOrders.map((w) => {
 const woNumber = w.woNo || w.woNumber || "";
 const woLabel = w.label
 ? `${w.label}${woNumber ? ` (${woNumber})` : ""}`
 : (woNumber || "Work Order");
 const projName = !filters.project
 ? (w.project?.projectName || w.projectName || w.project?.name)
 : null;
 return (
 <option key={w._id} value={w._id}>
 {woLabel}{projName ? ` – ${projName}` : ""}
 </option>
 );
 })}
 </select>
 </div>

 <div className="d-flex flex-column" style={{ minWidth: "160px", flex: "1 1 160px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Vendor</label>
 <select
 className="form-select form-select-sm"
 value={filters.vendor}
 onChange={(e) =>
 setFilters((prev) => ({
 ...prev,
 vendor: e.target.value,
 page: 1,
 }))
 }
 >
 <option value="">All Vendors</option>
 {vendors.map((v) => (
 <option key={v._id} value={v._id}>
 {v.companyName || v.name || v.email}
 </option>
 ))}
 </select>
 </div>

 <div className="d-flex flex-column" style={{ width: "130px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Status</label>
 <select
 className="form-select form-select-sm"
 value={filters.status}
 onChange={(e) =>
 setFilters((prev) => ({
 ...prev,
 status: e.target.value,
 page: 1,
 }))
 }
 >
 <option value="">All Statuses</option>
 <option value="Draft">Draft</option>
 <option value="Issued">Issued</option>
 <option value="Accepted">Accepted</option>
 <option value="Partially Delivered">Partially Delivered</option>
 <option value="Delivered">Delivered</option>
 <option value="Closed">Closed</option>
 </select>
 </div>

 <div className="d-flex flex-column" style={{ width: "130px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">From Date</label>
 <input
 type="date"
 className="form-control form-control-sm"
 value={filters.fromDate}
 onChange={(e) =>
 setFilters((prev) => ({
 ...prev,
 fromDate: e.target.value,
 page: 1,
 }))
 }
 />
 </div>

 <div className="d-flex flex-column" style={{ width: "130px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">To Date</label>
 <input
 type="date"
 className="form-control form-control-sm"
 value={filters.toDate}
 onChange={(e) =>
 setFilters((prev) => ({
 ...prev,
 toDate: e.target.value,
 page: 1,
 }))
 }
 />
 </div>

 {hasActiveFilters && (
 <div className="d-flex flex-column justify-content-end">
 <button
 type="button"
 className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
 style={{ height: "31px" }}
 onClick={handleResetFilters}
 title="Reset all filters"
 >
 <i className="fas fa-undo"></i>
 <span>Reset</span>
 </button>
 </div>
 )}

 <div className="ms-auto d-flex flex-column" style={{ width: "65px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Show</label>
 <select
 className="form-select form-select-sm"
 value={filters.limit}
 onChange={(e) =>
 setFilters((prev) => ({
 ...prev,
 limit: parseInt(e.target.value, 10),
 page: 1,
 }))
 }
 >
 <option value="10">10</option>
 <option value="25">25</option>
 <option value="50">50</option>
 <option value="100">100</option>
 </select>
 </div>
 </div>

 <Card.Body>
 {loading ? (
 <div className="text-center py-5">
 <div className="spinner-border text-primary"></div>
 <p>Loading purchase orders...</p>
 </div>
 ) : orders.length === 0 ? (
 <div className="text-center py-5">
 <p className="text-muted">No purchase orders found.</p>
 <Link
 to={`/purchase-orders/add${workOrderId ? `?workOrder=${workOrderId}` : ""}`}
 className="btn btn-primary"
>
 + Create PO
 </Link>
 </div>
 ) : (
 <div className="table-responsive">
 <Table hover className="align-middle">
 <thead>
 <tr>
 <th>#</th>
 <th>PO Number</th>
 <th>Vendor</th>
 <th>Indent Ref</th>
 <th>Project</th>
 <th>Total Amount</th>
 <th>Status</th>
 <th>Issue Date</th>
 <th className="text-center">Verified Copy</th>
 <th className="text-center">Actions</th>
 </tr>
 </thead>

 <tbody>
 {orders.map((item, i) => (
 <tr key={item._id}>
 <td>
 {(pagination.currentPage - 1) * filters.limit +
 i +
 1}
 </td>

 <td className="text-nowrap font-weight-bold">
 {item.poNumber || "—"}
 </td>

 <td style={{ fontSize: "0.9rem" }}>
 {item.vendor?.name ||
 item.vendor?.companyName ||
 item.vendor?.email ||
 (typeof item.vendor === "string"
 ? item.vendor
 : "N/A")}
 </td>

 <td style={{ fontSize: "0.9rem" }}>
 {getIndentReferenceText(item)}
 </td>

 <td style={{ fontSize: "0.9rem" }}>
 {item.project?.projectName ||
 item.workOrder?.project?.projectName ||
 item.project?.projectCode ||
 "—"}
 </td>


 <td className="text-nowrap">{formatCurrency(item.totalAmount)}</td>

 <td>
 <Badge bg={getStatusBadge(item.status)} className="rounded-pill">
 {item.status}
 </Badge>
 </td>

 <td className="text-nowrap">
 {formatDate(item.issueDate || item.createdAt)}
 </td>

 <td className="text-center">
 <VerifiedCopyActions
 id={item._id}
 type="purchase-orders"
 hasVerifiedCopy={!!item.verifiedCopy?.filePath}
 verifiedCopy={item.verifiedCopy}
 onUploadSuccess={() => fetchPurchaseOrders()}
 canUpload={canManagePO}
 />
 </td>

 <td className="text-center">
 <Dropdown>
 <Dropdown.Toggle
 variant=""
 className="btn-link i-false p-0 cursor-pointer"
 as="div"
 title="Click for more actions"
 >
 {svgMenu}
 </Dropdown.Toggle>
 <Dropdown.Menu align="end" as={PortalMenu}>
 <PermissionWrapper user={user} required={["read:pos"]}>
 <Dropdown.Item
 onClick={() => handleView(item._id)}
 title="View full PO details"
 >
 <i className="fas fa-eye me-2 text-primary"></i>
 View Details
 </Dropdown.Item>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["read:pos"]}>
 {item.attachments?.length > 0 && (
 <>
 <Dropdown.Divider />
 <h6 className="dropdown-header">Attachments</h6>
 {item.attachments.map((file, idx) => (
 <Dropdown.Item
 key={idx}
 href={getFileUrl(file.fileUrl)}
 target="_blank"
 rel="noopener noreferrer"
 >
 <i className="fas fa-file-alt me-2 text-info"></i>
 {file.fileName || `Attachment ${idx + 1}`}
 </Dropdown.Item>
 ))}
 <Dropdown.Divider />
 </>
 )}
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["read:pos"]}>
 <Dropdown.Item
 onClick={() => handlePreviewPDF(item._id)}
 disabled={previewingPdf}
 title="Preview system-generated PDF"
 >
 <i className="fas fa-eye me-2 text-info"></i>
 Preview PDF
 </Dropdown.Item>
 <Dropdown.Item
 onClick={() => handleDownloadPDF(item._id)}
 title="Download system-generated PDF"
 >
 <i className="fas fa-file-pdf me-2 text-danger"></i>
 Download PDF
 </Dropdown.Item>
 </PermissionWrapper>

 {item.status === "Draft" && (
 <>
 <PermissionWrapper user={user} required={["approve:pos"]}>
 <Dropdown.Item
 className="text-success"
 onClick={() => handleApprove(item._id)}
 disabled={approvingId === item._id}
 >
 <i className="fas fa-check-circle me-2"></i>
 {approvingId === item._id ? "Approving..." : "Approve"}
 </Dropdown.Item>
 </PermissionWrapper>
 <PermissionWrapper user={user} required={["reject:pos"]}>
 <Dropdown.Item
 className="text-danger"
 onClick={() => handleReject(item._id)}
 >
 <i className="fas fa-times-circle me-2"></i>
 Reject
 </Dropdown.Item>
 </PermissionWrapper>
 </>
 )}

 <PermissionWrapper user={user} required={["edit:pos"]}>
 <Dropdown.Item
 as={Link}
 to={`/purchase-orders/edit/${item._id}${
 workOrderId ? `?workOrder=${workOrderId}` : ""
 }`}
 >
 <i className="fas fa-edit me-2 text-warning"></i>
 Edit
 </Dropdown.Item>
 </PermissionWrapper>

 <Dropdown.Divider />
 <PermissionWrapper user={user} required={["delete:pos"]}>
 <Dropdown.Item
 className="text-danger"
 onClick={() => handleDelete(item._id)}
 >
 <i className="fas fa-trash me-2"></i>
 Delete
 </Dropdown.Item>
 </PermissionWrapper>
 </Dropdown.Menu>
 </Dropdown>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>

 {/* Pagination */}
 <AppPagination
 currentPage={pagination.currentPage}
 totalPages={pagination.totalPages}
 totalCount={pagination.totalCount}
 itemsLength={orders.length}
 limit={filters.limit}
 onPageChange={handlePageChange}
 />
 </div>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 <SignatureModal
 show={showSignModal}
 onHide={() => {
 setShowSignModal(false);
 setApproveId(null);
 }}
 onSave={handleSaveSignature}
 title="Client Signature for PO Approval"
 />

 {/* View Modal */}
 <Modal
 show={showModal}
 onHide={() => {
 setShowModal(false);
 setSelectedPO(null);
 }}
 size="lg"
 centered
 >
 <Modal.Header closeButton>
 <Modal.Title>PO: {selectedPO?.poNumber}</Modal.Title>
 </Modal.Header>

 <Modal.Body>
 {!selectedPO ? (
 <div className="text-center py-5">
 <div className="spinner-border text-primary"></div>
 </div>
 ) : (
 <>
 {/* Basic Info */}
 <Row className="mb-3">
 <Col md={6}>
 <p>
 <strong>PO Number:</strong>{" "}
 <span className="text-primary">
 {selectedPO.poNumber || "—"}
 </span>
 </p>
 <p>
 <strong>Vendor:</strong>{" "}
 {selectedPO.vendor?.name ||
 selectedPO.vendor?.companyName ||
 selectedPO.vendor?.email ||
 "N/A"}
 </p>
 <p>
 <strong>Indent Reference:</strong>{" "}
 {getIndentReferenceText(selectedPO)}
 </p>
 </Col>

 <Col md={6}>
 <p>
 <strong>Work Order:</strong>{" "}
{selectedPO.workOrder?.woNumber ||
 selectedPO.workOrder?.workOrderNumber ||
 selectedPO.workOrder?.scope ||
 "N/A"}

 </p>
 <p>
 <strong>Status: </strong>
 <Badge bg={getStatusBadge(selectedPO.status)} className="rounded-pill">
 {selectedPO.status}
 </Badge>
 </p>
 <p>
 <strong>Issue Date:</strong>{" "}
 {formatDate(selectedPO.issueDate || selectedPO.createdAt)}
 </p>
 <p>
 <strong>Expected Delivery:</strong>{" "}
 {formatDate(selectedPO.expectedDeliveryDate)}
 </p>
 </Col>
 </Row>

 {/* Documents */}
 {(selectedPO.purchaseOrderDocument ||
 selectedPO.quotationDocument || (selectedPO.attachments && selectedPO.attachments.length > 0)) && (
 <>
 <hr />
 <h5>Documents</h5>
 <div className="d-flex flex-column gap-2">
 {selectedPO.purchaseOrderDocument && (
 <div>
 <strong>PO Document:</strong>
 <FilePreview 
 file={{
 fileName: selectedPO.purchaseOrderDocument.fileName,
 fileUrl: selectedPO.purchaseOrderDocument.fileUrl
 }}
 onRemove={async () => {
 const confirm = await swal({
 title: "Delete PO Document?",
 text: "This will remove the PO document file.",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });
 if (!confirm) return;
 try {
 const res = await api.patch(`/purchase-orders/${selectedPO._id}/remove-file`, { field: 'purchaseOrderDocument' });
 if (res.data.success) {
 swal("Deleted!", "PO document removed", "success");
 handleView(selectedPO._id); // Refresh modal
 }
 } catch (err) {
 swal("Error", "Failed to remove PO document", "error");
 }
 }}
 />
 </div>
 )}

 {selectedPO.quotationDocument && (
 <div>
 <strong>Quotation Document:</strong>
 <FilePreview 
 file={{
 fileName: selectedPO.quotationDocument.fileName,
 fileUrl: selectedPO.quotationDocument.fileUrl
 }}
 onRemove={async () => {
 const confirm = await swal({
 title: "Delete Quotation?",
 text: "This will remove the quotation document file.",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });
 if (!confirm) return;
 try {
 const res = await api.patch(`/purchase-orders/${selectedPO._id}/remove-file`, { field: 'quotationDocument' });
 if (res.data.success) {
 swal("Deleted!", "Quotation removed", "success");
 handleView(selectedPO._id); // Refresh modal
 }
 } catch (err) {
 swal("Error", "Failed to remove quotation", "error");
 }
 }}
 />
 </div>
 )}

 {selectedPO.attachments?.map((doc, index) => (
 <div key={index}>
 <strong>Attachment {index + 1}:</strong>
 <FilePreview 
 file={{
 fileName: doc.fileName,
 fileUrl: doc.fileUrl
 }}
 onRemove={async () => {
 const confirm = await swal({
 title: "Delete Attachment?",
 text: "This will remove the attachment file.",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });
 if (!confirm) return;
 try {
 const res = await api.patch(`/purchase-orders/${selectedPO._id}/remove-file`, { 
 field: 'attachments',
 index: index
 });
 if (res.data.success) {
 swal("Deleted!", "Attachment removed", "success");
 handleView(selectedPO._id); // Refresh modal
 }
 } catch (err) {
 swal("Error", "Failed to remove attachment", "error");
 }
 }}
 />
 </div>
 ))}
 </div>
 </>
 )}

 <hr />

 {/* Amount */}
 <Row>
 <Col md={6}>
 <strong>Total Amount:</strong>{" "}
 <span className="text-success fs-5">
 {formatCurrency(selectedPO.totalAmount)}
 </span>
 </Col>
 <Col md={6}>
 <strong>Tax:</strong> {formatCurrency(selectedPO.tax)}
 </Col>
 </Row>

 {/* Items */}
 {selectedPO.items?.length > 0 && (
 <>
 <hr />
 <h6>Items</h6>
 <Table bordered size="sm">
 <thead className="table-light">
 <tr>
 <th>#</th>
 <th>Item</th>
 <th>Description</th>
 <th>Qty</th>
 <th>Unit</th>
 <th>Rate</th>
 <th>Amount</th>
 </tr>
 </thead>
 <tbody>
 {selectedPO.items.map((item, index) => (
 <tr key={index}>
 <td>{index + 1}</td>
                        <td>
                          <strong>{item.itemName}</strong>
                          {item.subItems && item.subItems.length > 0 && (
                            <div className="mt-1 ps-2 border-start border-2 border-primary" style={{ fontSize: "0.78rem" }}>
                              <div className="text-muted fw-bold">Sub-items:</div>
                              {item.subItems.map((s, sIdx) => (
                                <div key={sIdx} className="text-secondary">
                                  • {s.name} — {s.quantity} {s.unit || item.unit || ""} {s.remarks ? `(${s.remarks})` : ""}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
 <td>{item.description || "—"}</td>
 <td>{item.quantity}</td>
 <td>{item.unit}</td>
 <td>{formatCurrency(item.rate)}</td>
 <td>{formatCurrency(item.amount)}</td>
 </tr>
 ))}
 </tbody>
 </Table>
 </>
 )}

 {/* Terms */}
 {selectedPO.termsAndConditions && (
 <>
 <hr />
 <h6>Terms & Conditions</h6>
 <div
 dangerouslySetInnerHTML={{ __html: selectedPO.termsAndConditions }}
 className="text-muted"
 />
 </>
 )}
 </>
 )}
 </Modal.Body>

 <Modal.Footer>
 <Button variant="secondary" onClick={() => setShowModal(false)}>
 Close
 </Button>

 {selectedPO && (
 <Button
 as={Link}
 to={`/purchase-orders/edit/${selectedPO._id}${
 workOrderId ? `?workOrder=${workOrderId}` : ""
 }`}
 variant="primary"
 onClick={() => setShowModal(false)}
>
 <i className="fas fa-edit me-2"></i>Edit
 </Button>
 )}
 </Modal.Footer>
 </Modal>
 {/* =========================
 PDF PREVIEW LOADER
========================== */}
{previewingPdf && (
 <div
 className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
 style={{
 background: "rgba(0,0,0,0.4)",
 zIndex: 9999,
 }}
 >
 <div className="bg-white p-4 rounded shadow text-center">
 <div className="spinner-border text-primary mb-3"></div>
 <h6 className="mb-0">Preparing Purchase Order PDF…</h6>
 </div>
 </div>
)}

 </Fragment>
 );
};

export default PurchaseOrderList;
